import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Plus, Star, Trash2 } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";

import {
  createOffer,
  createSupplier,
  deleteOffer,
  listSuppliers,
  materialOffers,
  selectOffer,
} from "@/api/product-dev";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { apiErr, fmtDate, money } from "@/pages/product-dev/format";

const selectCls =
  "h-8 rounded-md border border-input bg-transparent px-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring";

/** Выбор поставщика: select из каталога + быстрое создание нового. */
export function SupplierSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (id: string) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const suppliers = useQuery({ queryKey: ["pdev", "suppliers"], queryFn: () => listSuppliers() });
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");

  const create = useMutation({
    mutationFn: () => createSupplier({ local_name: name.trim() }),
    onSuccess: (s) => {
      setName("");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["pdev", "suppliers"] });
      onChange(s.id);
    },
    onError: (e) => toast.error(apiErr(e, "Не удалось создать поставщика")),
  });

  return (
    <div className="flex items-center gap-1">
      <select className={selectCls} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">— поставщик —</option>
        {(suppliers.data ?? []).map((s) => (
          <option key={s.id} value={s.id}>
            {s.display_name}
          </option>
        ))}
      </select>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" size="icon" variant="outline" className="size-8 shrink-0">
            <Plus className="size-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-2" align="start">
          <form
            className="flex items-center gap-1"
            onSubmit={(e) => {
              e.preventDefault();
              if (name.trim()) create.mutate();
            }}
          >
            <Input
              autoFocus
              placeholder="Новый поставщик…"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-8"
            />
            <Button type="submit" size="sm" disabled={!name.trim()}>
              ОК
            </Button>
          </form>
        </PopoverContent>
      </Popover>
    </div>
  );
}

/** Форма добавления оффера по материалу (опц. в разрезе проекта). */
export function AddOfferForm({
  materialId,
  projectId,
  onDone,
}: {
  materialId: string;
  projectId?: string;
  onDone: () => void;
}): React.ReactElement {
  const [supplierId, setSupplierId] = useState("");
  const [price, setPrice] = useState("");
  const [conditions, setConditions] = useState("");

  const add = useMutation({
    mutationFn: () =>
      createOffer({
        material_id: materialId,
        supplier_id: supplierId || null,
        project_id: projectId ?? null,
        price: Number(price),
        conditions: conditions.trim() || null,
      }),
    onSuccess: () => {
      setPrice("");
      setConditions("");
      onDone();
      toast.success("Цена добавлена");
    },
    onError: (e) => toast.error(apiErr(e, "Не удалось добавить цену")),
  });

  return (
    <form
      className="flex flex-wrap items-center gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (price) add.mutate();
      }}
    >
      <SupplierSelect value={supplierId} onChange={setSupplierId} />
      <Input
        placeholder="цена ₽"
        value={price}
        onChange={(e) => setPrice(e.target.value)}
        className="h-8 w-24"
        inputMode="decimal"
      />
      <Input
        placeholder="условия (напр. от 10 000 шт, предоплата)…"
        value={conditions}
        onChange={(e) => setConditions(e.target.value)}
        className="h-8 w-72"
      />
      <Button type="submit" size="sm" variant="outline" disabled={!price || add.isPending}>
        Добавить цену
      </Button>
    </form>
  );
}

/** История цен по материалу: все офферы во времени + выбор утверждённой цены. */
export function OfferHistory({
  materialId,
  projectId,
}: {
  materialId: string;
  projectId?: string;
}): React.ReactElement {
  const qc = useQueryClient();
  const offers = useQuery({
    queryKey: ["pdev", "offers", materialId],
    queryFn: () => materialOffers(materialId),
  });
  const invalidate = (): void => {
    qc.invalidateQueries({ queryKey: ["pdev", "offers", materialId] });
    qc.invalidateQueries({ queryKey: ["pdev", "materials"] });
  };
  const pick = useMutation({
    mutationFn: (id: string) => selectOffer(id),
    onSuccess: () => {
      invalidate();
      toast.success("Цена зафиксирована на материале");
    },
    onError: (e) => toast.error(apiErr(e, "Не удалось выбрать")),
  });
  const del = useMutation({
    mutationFn: (id: string) => deleteOffer(id),
    onSuccess: invalidate,
  });

  return (
    <div className="space-y-2">
      {(offers.data ?? []).length > 0 ? (
        <table className="w-full text-xs">
          <thead className="text-left text-muted-foreground">
            <tr>
              <th className="py-1">Дата</th>
              <th className="py-1">Поставщик</th>
              <th className="py-1 text-right">Цена</th>
              <th className="py-1">Условия</th>
              <th className="py-1"></th>
            </tr>
          </thead>
          <tbody>
            {(offers.data ?? []).map((o) => (
              <tr key={o.id} className={`border-t ${o.is_selected ? "bg-emerald-50 dark:bg-emerald-950/30" : ""}`}>
                <td className="py-1">{fmtDate(o.quoted_at ?? o.created_at)}</td>
                <td className="py-1">{o.supplier_name ?? "—"}</td>
                <td className="py-1 text-right font-medium">{money(o.price, o.currency)}</td>
                <td className="py-1 text-muted-foreground">{o.conditions ?? "—"}</td>
                <td className="py-1">
                  <div className="flex items-center justify-end gap-1">
                    {o.is_selected ? (
                      <span className="inline-flex items-center gap-0.5 text-emerald-600 dark:text-emerald-400">
                        <Star className="size-3.5 fill-current" /> выбрана
                      </span>
                    ) : (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="size-6"
                        title="Выбрать как цену материала"
                        onClick={() => pick.mutate(o.id)}
                      >
                        <Check className="size-3.5" />
                      </Button>
                    )}
                    <Button
                      size="icon"
                      variant="ghost"
                      className="size-6"
                      onClick={() => del.mutate(o.id)}
                    >
                      <Trash2 className="size-3.5 text-muted-foreground hover:text-destructive" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="text-xs text-muted-foreground">Цен пока нет</div>
      )}
      <AddOfferForm materialId={materialId} projectId={projectId} onDone={invalidate} />
    </div>
  );
}
